import { Alert, Stack } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { FC } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ButtonRow } from '../../../components/global/ButtonRow/ButtonRow.tsx';
import { FlowStepWithContainer } from '../../../components/layout/FlowStepWithContainer.tsx';
import { CodeVerificationForm } from '../../../forms/auth/CodeVerification/CodeVerificationForm.tsx';
import { resetPassword } from '../../../services/promoterAuthentication.ts';
import { readResetPasswordState } from './resetPasswordState.ts';

export const CodeVerification: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { email } = readResetPasswordState(location.state);

  const { mutate: checkCode, isPending } = useMutation({
    mutationFn: async ({ code }: { code: string }) => {
      setErrorMessage(null);
      const response = await resetPassword.check({
        email,
        resetCode: code.toUpperCase(),
      });

      if ('type' in response) {
        throw new Error(response.message);
      }

      return { email, code: code.toUpperCase() };
    },
    onSuccess: ({ email, code }) => {
      void navigate('../password', { relative: 'path', state: { email, code } });
    },
    onError(error) {
      setErrorMessage(error.message);
    },
  });

  const { mutate: resendEmailVerification, isPending: resendEmailVerificationIsPending } = useMutation({
    mutationFn: async () => {
      await resetPassword.reset({
        email,
      });

      return email;
    },
  });

  return (
    <FlowStepWithContainer
      bodyContent={
        <Stack gap="sm">
          <CodeVerificationForm
            buttonSlot={
              <ButtonRow
                primaryButton={{
                  type: 'submit',
                  text: 'Next',
                  isPending,
                }}
                secondaryButton={{
                  onClick: () => resendEmailVerification(),
                  text: 'Send code again',
                  isPending: resendEmailVerificationIsPending,
                }}
              />
            }
            defaultValues={{ code: '' }}
            onSubmit={(data) => checkCode(data)}
          />
          {!!errorMessage && (
            <Alert color="gigi-apple-pink" p="xs" radius="xs">
              {errorMessage}
            </Alert>
          )}
        </Stack>
      }
      heading="Verify your code"
      progress={{ currentStep: 2, totalSteps: 3 }}
    />
  );
};
